import { ButtonKind, ButtonType } from "@spscommerce/ds-shared";
import * as React from "react";

import { SpsButton } from "../button/SpsButton";
import { SpsCard } from "../card/SpsCard";
import { SpsFieldset } from "./fieldset/SpsFieldset";
import { formArray, SpsFormArray } from "./hooks/formArray";
import { formControl, SpsFormControl } from "./hooks/formControl";
import { formGroup } from "./hooks/formGroup";
import { useForm } from "./hooks/useForm";
import { SpsLabel } from "./label/SpsLabel";
import { SpsForm } from "./SpsForm";
import { SpsValidators } from "./SpsValidators";
import { SpsTextInput } from "./text-input/SpsTextInput";

function SpsFormExample1(props) {
    const { "data-testid": testId } = props;

    function validateArray(value) {
        return value.find(color => color === "red")
        ? null
        : { "noRed": "One of the colors must be red." };
    }

    const formInitValue = {
        name: "foo",
        colors: ["red", "green", "blue"]
    };

    const form = useForm(formGroup({
        name: formControl(formInitValue.name, { validators: [SpsValidators.required] }),
        colors: formArray(formInitValue.colors.map(color => formControl(color)), { validators: [validateArray] })
    }));

    function submitHandler() {
        console.log("submit");
    }

    function reset(e) {
        e.preventDefault();
        form.reset();
    }

    function addColor(e) {
        e.preventDefault();
        form.get<SpsFormArray<string>>("colors").push(formControl(""));
    }

    return (
        <SpsCard>
            <SpsForm formGroup={form} onSubmit={submitHandler} data-testid={testId}>
                <div className="sps-row">
                    <div className="col-6">
                        <SpsLabel for={form.get("name")}>Name</SpsLabel>
                        <SpsTextInput formControl={form.get("name")}/>
                    </div>
                </div>
                <SpsFieldset legend="Colors" formArray={form.get("colors")}>
                    <div className="sps-row">
                        {(form.get("colors") as SpsFormArray<string>).members.map((color: SpsFormControl<string>, i) => (
                            <div className="col-3" key={i}>
                                <SpsLabel for={color}>Color</SpsLabel>
                                <SpsTextInput formControl={color}/>
                            </div>
                        ))}
                    </div>
                </SpsFieldset>
                <div className="sps-row">
                    <div className="col-3">
                        <SpsButton onClick={addColor} className="mr-1">Add Color</SpsButton>
                        <SpsButton onClick={reset} className="mr-1">Reset</SpsButton>
                        <SpsButton type={ButtonType.SUBMIT} kind={ButtonKind.CONFIRM}>Submit</SpsButton>
                    </div>
                </div>
            </SpsForm>
            <div className="sps-row">
                <div className="col-12">
                    <p>
                        <b>form.getValue():</b> {JSON.stringify(form.getValue())}<br/>
                        <b>form.get("colors[0]").getValue():</b> {form.get("colors[0]").getValue()}<br/>
                        <b>form.get("colors").errors:</b> {JSON.stringify(form.get("colors").errors)}
                    </p>
                </div>
            </div>
        </SpsCard>
    );
}

// @ts-ignore
SpsFormExample1.reactLive = {
    code: `
        function Component() {
            function validateArray(value) {
                return value.find(color => color === "red")
                ? null
                : { "noRed": true };
            }

            const storageOptions = ["32 GB", "64 GB", "128 GB", "256 GB", "512GB"];

            const formInitValue = {
                name: "foo",
                options: { a: true, b: false },
                storage: "64 GB",
                colors: ["orange", "green", "blue"]
            };

            const form = useForm(formGroup({
                name: formControl(formInitValue.name, { validators: [SpsValidators.required] }),
                options: formGroup({
                    a: formControl(formInitValue.options.a),
                    b: formControl(formInitValue.options.b)
                }),
                storage: formControl(formInitValue.storage),
                colors: formArray(formInitValue.colors.map(color => formControl(color)), { validators: [validateArray] })
            }));

            function submitHandler() {
                console.log("submit");
            }

            function reset(e) {
                e.preventDefault();
                form.reset();
            }

            function addColor(e) {
                e.preventDefault();
                form.get("colors").push(formControl(""));
            }

            function removeColor(e) {
                e.preventDefault();
                const colorsFormArray = form.get("colors");
                if (colorsFormArray.length > 0) {
                    colorsFormArray.removeAt(colorsFormArray.length - 1);
                }
            }

            return (
                <SpsCard>
                    <SpsForm formGroup={form} onSubmit={submitHandler}>
                        <div className="sps-row">
                            <div className="col-6">
                                <SpsLabel for={form.get("name")}
                                    help="Give this thing a distinct, easily recognizable name."
                                    errors={() => form.get("name").hasError("required") && "This field is required."}
                                >
                                    Name
                                </SpsLabel>
                                <SpsTextInput formControl={form.get("name")}/>
                            </div>
                            <div className="col-6">
                              <SpsLabel for={form.get("options")}>Options</SpsLabel>
                              <SpsInputGroup formGroup={form.get("options")}>
                                <SpsCheckbox label="Option A" formControl={form.get("options.a")}/>
                                <SpsCheckbox label="Option B" formControl={form.get("options.b")}/>
                              </SpsInputGroup>
                            </div>
                        </div>
                        <div className="sps-row">
                            <div className="col-12">
                                <SpsLabel for={form.get("storage")}>Storage</SpsLabel>
                                <SpsInputGroup>
                                    {storageOptions.map(opt =>
                                        <SpsRadioButton name="storage"
                                            label={opt}
                                            value={opt}
                                            formControl={form.get("storage")}
                                        />
                                    )}
                                </SpsInputGroup>
                            </div>
                        </div>
                        <SpsFieldset legend="Colors" formArray={form.get("colors")}
                            errors={() => form.get("colors").hasError("noRed") && "One of the colors must be red."}
                        >
                            <div className="sps-row">
                                {form.get("colors").members.map((color, i) => (
                                    <div className="col-3" key={i}>
                                        <SpsLabel for={color}>Color</SpsLabel>
                                        <SpsTextInput formControl={color}/>
                                    </div>
                                ))}
                            </div>
                        </SpsFieldset>
                        <div className="sps-row">
                            <div className="col-12">
                                <SpsButton onClick={addColor} className="mr-1">Add Color</SpsButton>
                                <SpsButton onClick={removeColor} className="mr-1">Remove Color</SpsButton>
                                <SpsButton onClick={reset} className="mr-1">Reset</SpsButton>
                                <SpsButton type="submit" kind="confirm">Submit</SpsButton>
                            </div>
                        </div>
                    </SpsForm>
                    <div className="sps-row">
                        <div className="col-12">
                            <p>
                                <b>form.getValue():</b> {JSON.stringify(form.getValue())}<br/>
                                <b>form.get("colors[0]").getValue():</b> {form.get("colors[0]").getValue()}<br/>
                                <b>form.get("colors").errors:</b> {JSON.stringify(form.get("colors").errors)}
                            </p>
                        </div>
                    </div>
                </SpsCard>
            );
        }
        render(<Component/>)
    `
};

export { SpsFormExample1 };
